import React from 'react';
import { Checkbox } from 'common/components';
import type { ProductRoles } from 'types/accessManager.type';
import { PRODUCT_LABELS, PRODUCT_ORDER, namesProducts, roleLabel } from '../../products';
import { ProductRolesPickerProps } from './ProductRolesPicker.types';
import styles from './ProductRolesPicker.module.scss';

// Roles by product: one row per product the caller manages, with the roles
// they may hand out there. Products and roles come from the backend
// (`manageable`), so the picker offers exactly what the API accepts. With one
// product the product is not named: it is just "Roles".
const ProductRolesPicker: React.FC<ProductRolesPickerProps> = ({
    manageable,
    value,
    onChange,
    disabled,
    errorText,
}) => {
    const products = PRODUCT_ORDER.filter((p) => (manageable[p]?.length ?? 0) > 0);
    const named = namesProducts(manageable);

    const toggle = (product: keyof ProductRoles, role: string, checked: boolean) => {
        const current = value[product] ?? [];
        const managed = manageable[product] ?? [];
        const next = checked ? [...current.filter((r) => r !== role), role] : current.filter((r) => r !== role);
        // Keep the backend's order, whatever order the boxes were clicked in.
        // A role the caller cannot hand out has no box and stays as it is: the
        // product's list is sent whole, so dropping it here would take it away.
        // (Today the backend lists nobody holding one — R7 — and refuses such
        // an edit; this keeps the screen from depending on that.)
        const ordered = [...managed.filter((r) => next.includes(r)), ...current.filter((r) => !managed.includes(r))];
        onChange({ ...value, [product]: ordered });
    };

    if (products.length === 0) {
        return <p className={styles.empty}>You cannot assign roles in this company.</p>;
    }

    return (
        <div className={styles.picker}>
            <span className={styles.title}>{named ? 'Products and roles' : 'Roles'}</span>
            {products.map((product) => (
                <fieldset key={product} className={named ? styles.product : styles.single} disabled={disabled}>
                    {named && <legend className={styles.productName}>{PRODUCT_LABELS[product]}</legend>}
                    <div className={styles.roles}>
                        {(manageable[product] ?? []).map((role) => (
                            <Checkbox
                                key={role}
                                size='sm'
                                label={roleLabel(role)}
                                checked={(value[product] ?? []).includes(role)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    toggle(product, role, e.target.checked)
                                }
                            />
                        ))}
                    </div>
                </fieldset>
            ))}
            {errorText && <span className={styles.error}>{errorText}</span>}
        </div>
    );
};

export default ProductRolesPicker;
